import { Button, Col, Dropdown, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { FormEvent, useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useCurrentClient } from "../../../auth/api/getCurrentClient";
import SentimentIndicator from "../../../../components/SentimentIndicatorSelector";
import { COLOR } from "../../../../config/constraits";
import { InputDatePicker } from "../../../../components/InputDatePicker";
import { createSentiment, getSentimentById, updateSentiment } from "../api/sentimentAnalysisAPI";
import { useNavigate, useParams } from "react-router-dom";
import { useAlert } from "../../../../hooks/useAlert";
import AlertMessage from "../../../../components/AlertMessage";
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import { SentimentFormDto } from "../types";
import { GenericSaveButton } from "../../../../components/common/GenericSaveButton";

export default function SentimentForm() {
    const { t } = useTranslation(["sentiment", "common", "claim"]);
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEditMode = !!id;
    const queryClient = useQueryClient();
    const { data: configClient } = useCurrentClient();
    const [selectedSentimentValue, setSelectedSentimentValue] = useState<number | null>(null);
    const [selectedSentimentType, setSelectedSentimentType] = useState<string>("");
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [criteria, setCriteria] = useState<string>("");
    const [examples, setExamples] = useState<string[]>([""]);
    const [loading, setLoading] = useState(false);
    const [loadingData, setLoadingData] = useState(isEditMode);
    const [errors, setErrors] = useState<{
        sentimentValue?: string;
        sentimentType?: string;
        startDate?: string;
        endDate?: string;
        criteria?: string;
    }>({});
    const { alert, hideAlert, showAlert, isVisible } = useAlert({ fromLocation: false });

    const sentimentTypeOptions = useMemo(() => ([
        { value: "positive", label: t("claim:newClaimMessageFeelingPositive"), color: COLOR.VERD },
        { value: "neutral", label: t("claim:newClaimMessageFeelingNeutral"), color: COLOR.TARONJA },
        { value: "negative", label: t("claim:newClaimMessageFeelingNegative"), color: COLOR.VERMELL },
    ]), [t]);
    const selectedSentimentTypeOption = sentimentTypeOptions.find((option) => option.value === selectedSentimentType);

    useEffect(() => {
        if (!isEditMode) return;

        const loadSentiment = async () => {
            setLoadingData(true);
            try {
                const data = await getSentimentById(Number(id));
                setSelectedSentimentValue(data.value);
                setSelectedSentimentType(data.type.toLowerCase());
                setStartDate(data.startDate ? new Date(data.startDate) : null);
                setEndDate(data.endDate ? new Date(data.endDate) : null);
                setCriteria(data.criteria ?? "");
                setExamples(data.examples?.length ? data.examples : [""]);
            } catch (error) {
                const fetchError = error as ApiErrorResponse;
                showAlert({
                    message: fetchError?.message ?? t("common:requestError.errorBadRequest"),
                    type: "danger",
                    duration: 8000
                });
            } finally {
                setLoadingData(false);
            }
        };

        loadSentiment();
    }, [id, isEditMode, showAlert, t]);

    const handleExampleChange = (index: number, value: string) => {
        const nextExamples = [...examples];
        nextExamples[index] = value;
        setExamples(nextExamples);
    };

    const handleAddExample = () => {
        setExamples((previous) => [...previous, ""]);
    };

    const handleRemoveExample = (indexToRemove: number) => {
        setExamples((previous) => {
            if (previous.length <= 1) {
                return previous;
            }

            return previous.filter((_, index) => index !== indexToRemove);
        });
    };

    const validateForm = () => {
        const newErrors: {
            sentimentValue?: string;
            sentimentType?: string;
            startDate?: string;
            endDate?: string;
            criteria?: string;
        } = {};

        if (selectedSentimentValue === null) {
            newErrors.sentimentValue = t("common:form.requiredFieldForm");
        } else if (!Number.isInteger(selectedSentimentValue)) {
            newErrors.sentimentValue = t(
                "sentiment:form.sentimentValueMustBeInteger",
                { defaultValue: "Sentiment value must be a valid integer" }
            );
        }
        if (!selectedSentimentType) {
            newErrors.sentimentType = t("common:form.requiredFieldForm");
        }
        //TODO: Descomentar cuando se introduzcan los datos de vigencia.
        // if (!startDate) {
        //     newErrors.startDate = t("common:form.requiredFieldForm");
        // }
        // if (startDate && endDate && startDate > endDate) {
        //     newErrors.endDate = t("common:form.messageErrorFinishDateBeforeStartDate");
        // }
        if (!criteria.trim()) {
            newErrors.criteria = t("common:form.requiredFieldForm");
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e?: FormEvent) => {
        e?.preventDefault();
        if (!validateForm() || loading || selectedSentimentValue === null) {
            return;
        }

        const payload: SentimentFormDto = {
            value: selectedSentimentValue.toString(),
            type: selectedSentimentType,
            validityStartDate: startDate || new Date(), //TODO: cuando se añada la vigencia será un campo obligatorio de momento enviamos la fecha de hoy
            validityEndDate: endDate,
            criteria: criteria.trim(),
            examples: examples.map((item) => item.trim()).filter((item) => !!item) || []
        };

        setLoading(true);
        try {
            if (isEditMode) {
                await updateSentiment(Number(id), payload);
            } else {
                await createSentiment(payload);
            }

            queryClient.invalidateQueries({ queryKey: ['sentiments'] });
            navigate("/admin/sentiment-analysis", {
                state: {
                    message: isEditMode
                        ? t("sentiment:form.updateSuccess", { defaultValue: "Sentiment updated successfully" })
                        : t("sentiment:form.saveSuccess", { defaultValue: "Sentiment created successfully" }),
                    type: "success"
                }
            });
        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            let message: string;
            if (fetchError?.code === "SENTIMENT_VALUE_ALREADY_ASSIGNED") {
                message = t("sentiment:form.errors.valueAlreadyAssigned");
            } else if (fetchError?.code === "SENTIMENT_RANGES_INVALID") {
                message = t("sentiment:form.errors.rangesInvalid");
            } else {
                message = fetchError?.message ?? t("common:requestError.errorBadRequest");
            }
            showAlert({ message, type: "danger", duration: 8000 });
        } finally {
            setLoading(false);
        }
    }

    const onGoBackClick = async () => {
        navigate("/admin/sentiment-analysis");
    }

    return (
        <>
            <Row className="mb-2">
                <Col xs={12} md={8}>
                    <h2>{isEditMode ? t("sentiment:form.editTitle", { defaultValue: "Edit sentiment analysis" }) : t("sentiment:form.title")}</h2>
                </Col>
                <Col xs={12} md={4} className="text-end mt-2 mt-md-0">
                    <GenericSaveButton
                        onClick={() => handleSubmit()}
                        onCancelClick={onGoBackClick}
                        cancelLabel={isEditMode ? t("common:buttons.buttonCancel") : t("common:buttons.buttonBack")}
                        saveLabel={loading ? t("common:buttons.buttonSaveingProgress") : t("common:buttons.buttonSave")}
                        disabled={loading || loadingData}
                    />
                </Col>
                <Col xs={12}>
                    {isVisible && (
                        <AlertMessage
                            message={alert!.message}
                            type={alert!.type}
                            onClose={hideAlert}
                        />
                    )}
                </Col>
            </Row>
             <Row>
                <Col>
                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col xs={4} md={6}>
                                <Row style={{ marginTop: '20px' }}>
                                    <Col xs={6} md={12}>
                                        <SentimentIndicator
                                            value={selectedSentimentValue}
                                            onChange={setSelectedSentimentValue}
                                            label={t("sentiment:form.sentimentAnalysisValue")}
                                            showSentimentLabel={false}
                                            showColorDot={false}
                                            minValue={configClient?.sentimentMinimumValue}
                                            maxValue={configClient?.sentimentMaximumValue}
                                            forceRangeOptions={true}
                                            disabled={loadingData || isEditMode}
                                        />
                                        {errors.sentimentValue && (
                                            <div className="invalid-feedback d-block">{errors.sentimentValue}</div>
                                        )}
                                    </Col>
                                </Row>
                            </Col>
                            <Col xs={6} md={6}>
                                <Row style={{ marginTop: '20px' }}>
                                    <Col xs={4} md={12}>
                                        <Form.Label>{t("sentiment:form.commentSentiment")}</Form.Label>
                                        <Dropdown>
                                            <Dropdown.Toggle
                                                variant="light"
                                                className="form-select dropdown-no-caret dropdown-select d-flex align-items-center justify-content-between text-start"
                                                disabled={loadingData || isEditMode}
                                            >
                                                <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                                    <span
                                                        style={{
                                                            width: "16px",
                                                            height: "16px",
                                                            borderRadius: "50%",
                                                            backgroundColor: selectedSentimentTypeOption?.color || "#e0e0e0",
                                                            flexShrink: 0,
                                                            border: selectedSentimentTypeOption ? "none" : "1px solid #ccc"
                                                        }}
                                                    />
                                                    {selectedSentimentTypeOption?.label ?? t("common:selectDefaultText")}
                                                </span>
                                            </Dropdown.Toggle>
                                            <Dropdown.Menu className="w-100 dropdown-menu-text-black">
                                                <Dropdown.Item onClick={() => setSelectedSentimentType("")}>
                                                    {t("common:selectDefaultText")}
                                                </Dropdown.Item>
                                                {sentimentTypeOptions.map((option) => (
                                                    <Dropdown.Item
                                                        key={option.value}
                                                        onClick={() => setSelectedSentimentType(option.value)}
                                                        className="d-flex align-items-center gap-2"
                                                    >
                                                        <span
                                                            style={{
                                                                width: "16px",
                                                                height: "16px",
                                                                borderRadius: "50%",
                                                                backgroundColor: option.color,
                                                                flexShrink: 0
                                                            }}
                                                        />
                                                        {option.label}
                                                    </Dropdown.Item>
                                                ))}
                                            </Dropdown.Menu>
                                        </Dropdown>
                                        {errors.sentimentType && (
                                            <div className="invalid-feedback d-block">{errors.sentimentType}</div>
                                        )}
                                    </Col>
                                </Row>
                            </Col>
                        </Row>
                        {/* TODO: Mostrar estos campos cuando se implemente la vigencia */}
                        <Row hidden>
                            <Col xs={4} md={6}>
                                <Row style={{ marginTop: '20px' }}>
                                    <Col xs={6} md={12}>
                                        <InputDatePicker
                                            label={t("sentiment:form.validStartDate")}
                                            value={startDate}
                                            onChange={(value) => setStartDate(value)}
                                            hasError={!!errors.startDate}
                                            errorMessage={errors.startDate}
                                            disabled={loadingData}
                                        />
                                    </Col>
                                </Row>
                            </Col>
                            <Col xs={6} md={6}>
                                <Row style={{ marginTop: '20px' }}>
                                    <Col xs={4} md={12}>
                                        <InputDatePicker
                                            label={t("sentiment:form.validEndDate")}
                                            value={endDate}
                                            onChange={(value) => setEndDate(value)}
                                            hasError={!!errors.endDate}
                                            errorMessage={errors.endDate}
                                            disabled={loadingData}
                                        />
                                    </Col>
                                </Row>
                            </Col>
                        </Row>

                        <Row style={{ marginTop: '20px' }}>
                            <Col xs={4} md={12}>
                                <Form.Label>{t("sentiment:form.criteria")}</Form.Label>
                                <Form.Control
                                        type="text"
                                        value={criteria}
                                        onChange={(e) => setCriteria(e.target.value)}
                                        isInvalid={!!errors.criteria}
                                        disabled={loadingData}
                                    />
                                <Form.Control.Feedback type="invalid">
                                    {errors.criteria}
                                </Form.Control.Feedback>
                            </Col>
                        </Row>
                        {examples.map((example, index) => (
                            <Row style={{ marginTop: '20px' }} key={`sentiment-example-${index}`}>
                                <Col xs={12} md={12}>
                                    <Form.Group>
                                        <Form.Label>{t("sentiment:form.sentimentAnalysisExample")}</Form.Label>
                                        <div className="d-flex gap-2 align-items-start">
                                            <Form.Control
                                                type="text"
                                                value={example}
                                                onChange={(e) => handleExampleChange(index, e.target.value)}
                                                disabled={loadingData}
                                            />
                                            <Button
                                                variant="light"
                                                type="button"
                                                onClick={() => handleRemoveExample(index)}
                                                disabled={examples.length === 1 || loadingData}
                                                className="rounded-circle d-flex align-items-center justify-content-center border-0 p-0"
                                                style={{ width: "32px", height: "32px", backgroundColor: "#e9ecef", color: "#6c757d", fontSize: "18px", lineHeight: 1 }}
                                                aria-label={t("common:buttons.buttonDelete")}
                                            >
                                                -
                                            </Button>
                                            {index === examples.length - 1 && (
                                                <Button
                                                    variant="light"
                                                    type="button"
                                                    onClick={handleAddExample}
                                                    disabled={loadingData}
                                                    className="rounded-circle d-flex align-items-center justify-content-center border-0 p-0"
                                                    style={{ width: "32px", height: "32px", backgroundColor: "#e9ecef", color: "#6c757d", fontSize: "18px", lineHeight: 1 }}
                                                    aria-label={t("sentiment:form.addExample")}
                                                >
                                                    +
                                                </Button>
                                            )}
                                        </div>
                                    </Form.Group>
                                </Col>
                            </Row>
                        ))}
                    </Form>
                </Col>
            </Row>

        </>
    );
}


